import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import {
    entityProfileTypeLabels,
    industryLabels,
} from "@components/entity/constants";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { getMultiSelectTriggerValue } from "@components/onboarding/lib";
import styles from "./ChartOfAccountsTemplateModal.module.scss";

const entityTypeItems = Object.values(EntityProfileType)
    .filter((value) => value !== EntityProfileType.NO_LEGAL_ENTITY)
    .map((value) => ({
        value,
        label: entityProfileTypeLabels[value],
    }));

const industryItems = Object.values(EntityProfileIndustry).map((value) => ({
    value,
    label: industryLabels[value],
}));

const allIndustryValues = industryItems.map((item) => item.value);

const industrySections = [
    { title: "All industries", items: industryItems, selectAll: true },
];

const basisItems = Object.values(AccountingLedgerBasis).map((value) => ({
    value,
    label: getAccountingBasisLabel(value),
}));

const selectControlTrigger = (
    <Picker.Button variant="primary" size="lg" block />
);

interface Props {
    entityLegalType: EntityProfileType | null;
    isEntityTypeLocked: boolean;
    industries: EntityProfileIndustry[];
    basis: AccountingLedgerBasis[];
    onEntityTypeChange: (value: EntityProfileType | null) => void;
    onIndustriesChange: (value: EntityProfileIndustry[]) => void;
    onBasisChange: (value: AccountingLedgerBasis[]) => void;
}

export const ChartOfAccountsTemplateHeaderFields = ({
    entityLegalType,
    isEntityTypeLocked,
    industries,
    basis,
    onEntityTypeChange,
    onIndustriesChange,
    onBasisChange,
}: Props) => (
    <div className={styles.fieldGrid}>
        <FormikField
            name="entityLegalType"
            label="Entity type"
            control="button"
        >
            {() => (
                <Tooltip
                    content={
                        isEntityTypeLocked
                            ? "This template is applied to entities, so its entity type can't be updated"
                            : undefined
                    }
                >
                    <Picker.Select
                        items={entityTypeItems}
                        value={entityLegalType}
                        onValueChange={onEntityTypeChange}
                        placeholder="Select"
                        trigger={selectControlTrigger}
                        disabled={isEntityTypeLocked}
                    />
                </Tooltip>
            )}
        </FormikField>

        <FormikField
            name="industries"
            label="Industry"
            control="button"
            optional
        >
            {() => {
                const selectedIndustries = new Set(industries);
                const areAllIndustriesSelected =
                    allIndustryValues.length > 0 &&
                    allIndustryValues.every((industry) =>
                        selectedIndustries.has(industry),
                    );

                return (
                    <Picker.Select
                        multiple
                        sections={industrySections}
                        value={industries}
                        onValueChange={onIndustriesChange}
                        renderValue={() =>
                            areAllIndustriesSelected
                                ? "All industries"
                                : getMultiSelectTriggerValue(
                                      industries,
                                      industryItems.length,
                                      (value) => industryLabels[value],
                                  )
                        }
                        placeholder="Select"
                        trigger={selectControlTrigger}
                    />
                );
            }}
        </FormikField>

        <FormikField name="basis" label="Basis" control="button" optional>
            {() => (
                <Picker.Select
                    multiple
                    items={basisItems}
                    value={basis}
                    onValueChange={onBasisChange}
                    renderValue={() =>
                        getMultiSelectTriggerValue(
                            basis,
                            basisItems.length,
                            (value) => getAccountingBasisLabel(value),
                        )
                    }
                    placeholder="Select"
                    trigger={selectControlTrigger}
                />
            )}
        </FormikField>
    </div>
);
